import React, { useContext, useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  Toolbar,
  Typography,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ApartmentOutlinedIcon from '@material-ui/icons/ApartmentOutlined';
import { I18nContext } from 'translations';
import { getCostCenter } from 'context/express/actions';

const ModalCostCenter = ({ open, onClose, companyId }) => {
  const { translate } = useContext(I18nContext);
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open && companyId) {
      setLoading(true);
      getCostCenter(companyId).then(({ costCenter }) => {
        setCostCenters(costCenter || []);
        setLoading(false);
      });
    }
    if (!open) setSearch('');
  }, [open, companyId]);

  const filtered = useMemo(
    () => costCenters.filter((c) => c.name.toLowerCase().includes(search.toLowerCase())),
    [costCenters, search]
  );

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <Toolbar style={{ justifyContent: 'center', borderBottom: '1px solid #f0f0f0' }}>
        <IconButton
          edge="end"
          color="primary"
          onClick={onClose}
          aria-label="close"
          style={{ position: 'absolute', right: 20, top: 10 }}
        >
          <CloseIcon />
        </IconButton>
        <div style={{ textAlign: 'center' }}>
          <Typography variant="h6" color="primary">
            {translate('app.bookings.filter.cost_center')}
          </Typography>
          {!loading && (
            <Typography variant="caption" color="textSecondary">
              {costCenters.length} {costCenters.length === 1 ? translate('app.bookings.filter.cost_center') : `${translate('app.bookings.filter.cost_center')}s`}
            </Typography>
          )}
        </div>
      </Toolbar>

      <DialogContent style={{ padding: '16px 24px 24px', height: 480, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress color="primary" size={32} />
          </div>
        ) : costCenters.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9e9e9e' }}>
            <ApartmentOutlinedIcon style={{ fontSize: 48, marginBottom: 8, opacity: 0.4 }} />
            <Typography variant="body2" color="textSecondary">
              {translate('thereIsNoData')}
            </Typography>
          </div>
        ) : (
          <>
            <TextField
              size="small"
              variant="outlined"
              placeholder={translate('search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ marginBottom: 12, flexShrink: 0, width: '50%', alignSelf: 'flex-end' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#9e9e9e', fontSize: '1.2rem' }} />
                  </InputAdornment>
                )
              }}
            />
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {filtered.length === 0 ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    {translate('noMatches')}
                  </Typography>
                </div>
              ) : (
                <List dense disablePadding>
                  {filtered.map((center, index) => (
                    <React.Fragment key={center._id}>
                      <ListItem style={{ padding: '8px 0' }}>
                        <ListItemAvatar>
                          <Avatar style={{ backgroundColor: '#ede7f6', color: '#6200ea', width: 36, height: 36, fontSize: '0.9rem', fontWeight: 600 }}>
                            {center.name.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={center.name}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                      {index < filtered.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModalCostCenter;
